import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";

import { type PerformanceMetric, PerformanceMetricsService } from "@/services/PerformanceMetricsService";

interface DebugMetricsModalProps {
  open: boolean;
  onClose: () => void;
}

const formatDuration = (ms: number): string => {
  if (ms < 1000) {
    return `${Math.round(ms)}ms`;
  }
  return `${(ms / 1000).toFixed(2)}s`;
};

const formatTimestamp = (timestamp: number): string => {
  const date = new Date(timestamp);
  return date.toLocaleString();
};

const formatTarget = (target?: string): string => {
  if (!target) return "-";
  if (target.length > 50) {
    return target.substring(0, 50) + "...";
  }
  return target;
};

const TIME_FILTERS = [
  { label: "Last 5 minutes", value: 5 * 60 * 1000 },
  { label: "Last 15 minutes", value: 15 * 60 * 1000 },
  { label: "Last hour", value: 60 * 60 * 1000 },
  { label: "Last 24 hours", value: 24 * 60 * 60 * 1000 },
  { label: "All time", value: null },
];

export default function DebugMetricsModal({ open, onClose }: DebugMetricsModalProps) {
  const [metrics, setMetrics] = useState<PerformanceMetric[]>([]);
  const [filteredMetrics, setFilteredMetrics] = useState<PerformanceMetric[]>([]);
  const [actionFilter, setActionFilter] = useState<string>("all");
  const [timeFilter, setTimeFilter] = useState<number | null>(TIME_FILTERS[2].value);
  const [uniqueActions, setUniqueActions] = useState<string[]>([]);
  const [statistics, setStatistics] = useState<{
    count: number;
    avgDuration: number;
    minDuration: number;
    maxDuration: number;
    totalDuration: number;
  } | null>(null);

  const loadMetrics = async () => {
    const allMetrics = await PerformanceMetricsService.getMetrics({ limit: 500 });
    setMetrics(allMetrics);

    const actions = Array.from(new Set(allMetrics.map((m) => m.action)));
    setUniqueActions(actions.sort());
  };

  useEffect(() => {
    if (open) {
      loadMetrics();
    }
  }, [open]);

  useEffect(() => {
    let filtered = [...metrics];

    if (timeFilter !== null) {
      const cutoffTime = Date.now() - timeFilter;
      filtered = filtered.filter((m) => m.startTime >= cutoffTime);
    }

    if (actionFilter !== "all") {
      filtered = filtered.filter((m) => m.action === actionFilter);
    }

    setFilteredMetrics(filtered);

    if (filtered.length > 0) {
      const durations = filtered.map((m) => m.duration);
      const totalDuration = durations.reduce((sum, d) => sum + d, 0);
      const avgDuration = totalDuration / durations.length;
      const minDuration = Math.min(...durations);
      const maxDuration = Math.max(...durations);

      setStatistics({
        count: filtered.length,
        avgDuration,
        minDuration,
        maxDuration,
        totalDuration,
      });
    } else {
      setStatistics(null);
    }
  }, [metrics, actionFilter, timeFilter]);

  const handleClearMetrics = async () => {
    await PerformanceMetricsService.clearMetrics();
    setMetrics([]);
    setFilteredMetrics([]);
    setStatistics(null);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Performance Metrics</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", gap: 2, marginBottom: 2 }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Action Filter</InputLabel>
            <Select value={actionFilter} label="Action Filter" onChange={(e) => setActionFilter(e.target.value)}>
              <MenuItem value="all">All Actions</MenuItem>
              {uniqueActions.map((action) => (
                <MenuItem key={action} value={action}>
                  {action}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Time Range</InputLabel>
            <Select
              value={timeFilter ?? "null"}
              label="Time Range"
              onChange={(e) => setTimeFilter(e.target.value === "null" ? null : Number(e.target.value))}
            >
              {TIME_FILTERS.map((filter) => (
                <MenuItem key={filter.label} value={filter.value ?? "null"}>
                  {filter.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button variant="outlined" color="error" onClick={handleClearMetrics} size="small">
            Clear History
          </Button>
        </Box>

        {statistics && (
          <Box
            sx={{
              display: "flex",
              gap: 3,
              marginBottom: 2,
              padding: 2,
              backgroundColor: "#f5f5f5",
              borderRadius: 1,
            }}
          >
            <Box>
              <Typography variant="caption" color="textSecondary">
                Count
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {statistics.count}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="textSecondary">
                Avg Duration
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {formatDuration(statistics.avgDuration)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="textSecondary">
                Min Duration
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {formatDuration(statistics.minDuration)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="textSecondary">
                Max Duration
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {formatDuration(statistics.maxDuration)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="textSecondary">
                Total Duration
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {formatDuration(statistics.totalDuration)}
              </Typography>
            </Box>
          </Box>
        )}

        <TableContainer sx={{ maxHeight: 500 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Action</TableCell>
                <TableCell>Target</TableCell>
                <TableCell align="right">Duration</TableCell>
                <TableCell>Timestamp</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredMetrics.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography variant="body2" color="textSecondary">
                      No metrics available
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredMetrics.map((metric) => (
                  <TableRow key={metric.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "12px" }}>
                        {metric.action}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "11px", color: "#666" }}>
                        {formatTarget(metric.target)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: "monospace",
                          fontSize: "12px",
                          fontWeight: "bold",
                          color: metric.duration > 5000 ? "#d32f2f" : metric.duration > 2000 ? "#ff9800" : "#4caf50",
                        }}
                      >
                        {formatDuration(metric.duration)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: "11px", color: "#666" }}>
                        {formatTimestamp(metric.startTime)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
